/**
 * Vocabulaire métier de TechPlanner : valeurs stockées (anglais, stables)
 * et libellés affichés (français).
 */

export const ROLES = ["admin", "technician"] as const;
export type Role = (typeof ROLES)[number];

export const INTERVENTION_STATUSES = ["planned", "in_progress", "done", "cancelled"] as const;
export type InterventionStatus = (typeof INTERVENTION_STATUSES)[number];

export const STATUS_LABELS: Record<InterventionStatus, string> = {
  planned: "À faire",
  in_progress: "En cours",
  done: "Terminée",
  cancelled: "Annulée"
};

/** Une intervention dans l'un de ces statuts est figée : la régénération d'un contrat n'y touche plus. */
export const FROZEN_STATUSES: readonly InterventionStatus[] = ["done", "cancelled"];

export function isFrozenStatus(status: InterventionStatus): boolean {
  return FROZEN_STATUSES.includes(status);
}

export const PRIORITIES = ["low", "normal", "high", "urgent"] as const;
export type Priority = (typeof PRIORITIES)[number];

export const PRIORITY_LABELS: Record<Priority, string> = {
  low: "Basse",
  normal: "Normale",
  high: "Haute",
  urgent: "Urgente"
};

/** Bornes des compteurs de fréquence (mois / jours) d'un équipement ou d'un contrat. */
export const FREQUENCY_MAX_MONTHS = 36;
export const FREQUENCY_MAX_DAYS = 365;

export const CONTRACT_STATUSES = ["active", "ended"] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

/** Nombre maximal de passages générés pour un contrat (garde-fou). */
export const MAX_CONTRACT_OCCURRENCES = 60;

/** Photos au plus sur un bon d'intervention. */
export const MAX_REPORT_PHOTOS = 6;

/** « Tous les 4 mois », « Tous les 3 mois et 15 jours », « Tous les 15 jours », « Tous les mois ». */
export function formatFrequency(months: number, days = 0): string {
  if (!Number.isInteger(months)) {
    // Anciennes fréquences en demi-mois (3,5 mois).
    return `Tous les ${Math.trunc(months)} mois ½${days ? ` et ${days} jour${days > 1 ? "s" : ""}` : ""}`;
  }
  const parts: string[] = [];
  if (months) parts.push(months === 1 ? "mois" : `${months} mois`);
  if (days) parts.push(days === 1 && !months ? "jour" : `${days} jour${days > 1 ? "s" : ""}`);
  if (!parts.length) return "Pas d'entretien périodique";
  return `Tous les ${parts.join(" et ")}`;
}

/** Code d'une société : minuscules, chiffres et tirets, retenu par le téléphone via le lien d'invitation. */
export const COMPANY_CODE_PATTERN = /^[a-z0-9]([a-z0-9-]{0,38}[a-z0-9])?$/;

/** Codes réservés : sous-domaines et adresses de TechPlanner, ou trompeurs. */
export const RESERVED_COMPANY_CODES = [
  "admin", "api", "app", "aide", "blog", "contact", "demo", "dev", "docs", "help", "inscription", "login", "mail",
  "public", "sodiaal", "staging", "static", "stock", "support", "techplanner", "test", "v2", "v3", "www"
] as const;

/** Code proposé à partir du nom de la société : « Dupont Chauffage & Fils » → « dupont-chauffage-fils ». */
export function suggestCompanyCode(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/g, "");
}

export function isCompanyCodeAllowed(code: string): boolean {
  return COMPANY_CODE_PATTERN.test(code) && !(RESERVED_COMPANY_CODES as readonly string[]).includes(code);
}

export const COMPANY_STATUSES = ["pending", "active", "suspended"] as const;
export type CompanyStatus = (typeof COMPANY_STATUSES)[number];

/** Tutoriel de démarrage de l'admin, dans l'ordre. */
export const TUTORIAL_STEPS = ["branding", "equipment", "team", "client", "contract", "install"] as const;
export type TutorialStep = (typeof TUTORIAL_STEPS)[number];
